import { Building2, CalendarClock, Check, ShieldCheck, UserPlus, Users } from 'lucide-react'
import { cn } from '@/lib/cn'
import { AuthShowcase } from '@/features/auth/components/AuthLayout'
import { LOGIN_COPY } from '@/features/auth/constants/loginCopy'
import { tenantHostLabel } from '@/lib/tenantHost'
import type { SignupPreview } from '@/features/auth/types/signupPreview'

const NEXT_STEPS = [
  { label: 'Invite HR, managers, and staff', icon: UserPlus },
  { label: 'Set up shifts and leave policies', icon: CalendarClock },
] as const

const PANEL_POINTS = [
  { label: 'You become the owner admin', icon: ShieldCheck },
  { label: 'Invite your team after setup', icon: Users },
  { label: 'Add more companies any time', icon: Building2 },
] as const

function initialsOf(...parts: string[]): string {
  return parts
    .map((part) => part.trim().charAt(0))
    .join('')
    .toUpperCase()
}

/** A preview of the workspace being created that fills in as the form is typed. */
function WorkspacePreview({ preview }: { preview: SignupPreview }): React.ReactElement {
  const name = preview.organizationName.trim()
  const slug = preview.organizationSlug.trim()
  const ownerName = `${preview.firstName} ${preview.lastName}`.trim()
  const ownerInitials = initialsOf(preview.firstName, preview.lastName)
  const address = slug ? (tenantHostLabel(slug) ?? slug) : 'your-workspace'
  const isReady = Boolean(name && slug && ownerName && preview.email.trim())

  return (
    <div className="login-vignette w-full max-w-[28rem]">
      <div className="rounded-[1.75rem] bg-white p-5 text-[var(--public-ink)] shadow-[0_40px_90px_-30px_rgba(0,0,0,0.75)] sm:p-6">
        <div className="flex items-start gap-3.5">
          <span
            className={cn(
              'flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl font-display text-xl font-semibold transition-colors duration-300',
              name ? 'bg-primary text-white' : 'bg-[var(--public-soft)] text-primary/50'
            )}
            aria-hidden
          >
            {name ? name.charAt(0).toUpperCase() : <Building2 className="h-5 w-5" />}
          </span>
          <div className="min-w-0 flex-1">
            <p
              className={cn(
                'truncate font-display text-xl font-semibold leading-tight tracking-[-0.02em]',
                !name && 'text-[var(--public-border-strong)]'
              )}
            >
              {name || 'Your organization'}
            </p>
            <p className="mt-1 truncate text-xs text-[var(--public-muted)]">{address}</p>
          </div>
          <span
            className={cn(
              'mt-0.5 inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold transition-colors duration-300',
              isReady ? 'bg-success/10 text-success' : 'bg-[var(--public-soft)] text-[var(--public-muted)]'
            )}
          >
            <span
              className={cn('h-1.5 w-1.5 rounded-full', isReady ? 'bg-success' : 'bg-[var(--public-border-strong)]')}
              aria-hidden
            />
            {isReady ? 'Ready' : 'Draft'}
          </span>
        </div>

        <div className="mt-5 flex items-center gap-3 rounded-2xl bg-[var(--public-bg)] px-3.5 py-3">
          <span
            className={cn(
              'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold',
              ownerInitials ? 'bg-[var(--public-ink)] text-white' : 'border border-dashed border-[var(--public-border-strong)] text-transparent'
            )}
            aria-hidden
          >
            {ownerInitials || '·'}
          </span>
          <span className="min-w-0 flex-1">
            <span className={cn('block truncate text-sm font-semibold leading-tight', !ownerName && 'text-[var(--public-border-strong)]')}>
              {ownerName || 'Owner name'}
            </span>
            <span className="block truncate text-xs leading-tight text-[var(--public-muted)]">
              {preview.email.trim() || 'owner@company.com'}
            </span>
          </span>
          <span className="shrink-0 rounded-full bg-white px-2 py-0.5 text-[0.7rem] font-semibold text-primary ring-1 ring-[var(--public-border)]">
            Owner
          </span>
        </div>

        <ol className="mt-5 space-y-1 border-t border-[var(--public-border)] pt-4">
          <li className="flex items-center gap-3 py-1.5 text-sm">
            <span
              className={cn(
                'flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-colors duration-300',
                isReady ? 'bg-success text-white' : 'bg-accent text-white'
              )}
              aria-hidden
            >
              {isReady ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : <span className="h-1.5 w-1.5 rounded-full bg-white" />}
            </span>
            <span className="font-semibold">Create your workspace</span>
          </li>
          {NEXT_STEPS.map((step) => (
            <li key={step.label} className="flex items-center gap-3 py-1.5 text-sm text-[var(--public-muted)]">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full ring-1 ring-[var(--public-border)]" aria-hidden>
                <step.icon className="h-3.5 w-3.5" />
              </span>
              {step.label}
            </li>
          ))}
        </ol>
      </div>
    </div>
  )
}

/** Brand panel beside the sign-up form. */
export function SignupShowcase({ preview }: { preview: SignupPreview }): React.ReactElement {
  return (
    <AuthShowcase
      headline="Bring your whole team"
      headlineMuted="into one workspace."
      subhead={LOGIN_COPY.subhead}
      footer={
        <ul className="grid grid-cols-1 gap-x-8 gap-y-3 xl:grid-cols-3" aria-label="What happens next">
          {PANEL_POINTS.map((point) => (
            <li key={point.label} className="flex items-center gap-2.5 text-sm text-white/75">
              <point.icon className="h-4 w-4 shrink-0 text-white/45" aria-hidden />
              <span className="min-w-0">{point.label}</span>
            </li>
          ))}
        </ul>
      }
    >
      <WorkspacePreview preview={preview} />
    </AuthShowcase>
  )
}
